import { createFileRoute, Link } from "@tanstack/react-router";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { Button } from "@/components/ui/button";
import ChangeLanguage from "@/components/ui/shared/change-language";
import { Alert } from "@/components/ui/alert";
import { MailCheck } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/(onboarding)/auth/verify-account")({
  component: ActivateAccount,
});

function ActivateAccount() {
  const { t } = useTranslate();
  const store = useAppStore();

  useEffect(() => {
    store.auth.fetchSession();
  }, []);

  const handleResendEmail = () => {
    store.loading.start("resend");
    store.errors.reset();
    store.auth
      .sendVerificationEmail()
      .then(() => store.display.show("resend_success"))
      .catch(store.errors.catch)
      .finally(() => store.loading.stop("resend"));
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Left side: branding/illustration */}
      <div className="w-1/2 hidden md:block relative overflow-hidden  border-r border-gray-100">
        <div className="absolute top-8 left-8">
          <img src="/assets/images/logo.svg" alt="Logo" width={140} />
        </div>
        <div className="flex items-center justify-center h-screen">
          <img
            src="/assets/images/verify-account.svg"
            alt="Activate Account"
            className="w-2/3"
          />
        </div>
      </div>

      {/* Right side: content */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 bg-white relative">
        <div className="absolute top-8 right-8">
          <ChangeLanguage />
        </div>

        <div className="w-full max-w-md text-center">
          {/* Icon visual */}
          <div className="mx-auto w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6">
            <MailCheck className="w-8 h-8 text-primary-600" />
          </div>

          <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-3">
            {t("Check your inbox")}
          </h1>

          <p className="text-gray-600 mb-8 leading-relaxed">
            {t(
              "We've sent an activation link to your email. Click it to verify your account and start using SalesBox. If you don't see it, please check your spam folder."
            )}
          </p>

          <Alert
            name="resend_success"
            message={t("A new activation link has been sent to your email.")}
            className="mb-6"
          />

          <div>{store.errors.render()}</div>

          <div className="space-y-6">
            <Button
              className="w-full h-11"
              onClick={handleResendEmail}
              loading={store.loading.status.resend}
              disabled={store.loading.status.resend}
            >
              {t("Resend Activation Link")}
            </Button>

            <div className="pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                {t("Entered the wrong email?")}{" "}
                <Link
                  to="/auth/register"
                  className="font-semibold text-primary-600 hover:underline"
                >
                  {t("Sign up again")}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
